import React, { useContext, useState, useMemo, useEffect } from 'react'
import styled, { css } from 'styled-components'
import queryString from 'query-string'
import * as R from 'ramda'
import GatsbyImage from 'gatsby-image'
import loadable from '@loadable/component'
import ReviewStarsStore from '../shared/ReviewStarsStore'
import ProductThumbnail from '../shared/ProductThumbnail'
import { LayoutContext, useUI } from '../../contexts'
import { SiteLink } from '../shared/SiteLink'
import {
  useCollectionGenderSelectedTracking,
  useCollectionAnchorClickedTracking,
} from '../../hooks/tracking'

import ProductLabelLogo from '../product/ProductLabelLogo'
import { getAvailable } from '../../utils'
import BreadCrumb from '../shared/BreadCrumb'
import { useGlobal } from '../../hooks/useGlobal'
// import theme from '../../styles/theme'
// import { useIsMobile } from '../../hooks/useIsMobile'

const RichText = loadable(() => import('../RichText'))
const Video = loadable(() => import('../shared/Video'))

const CollectionProducts = ({
  name,
  location,
  position,
  onViewDetails,
  detail,
  search,
  hasSubCollection,
  products,
  title,
  partnerText,
  partnerLogoLink,
  partnerLogo,
  image,
  video,
  information,
  trustpilotStars,
  totalNumberOfReviews,
  alternativeLayout,
}) => {
  const [blackFridayLayout, setBlackFridayLayout] = useState(false)

  // const isMobile = useIsMobile()
  const { currency, node_locale } = useContext(LayoutContext)
  const [filteredProducts, setFilteredProducts] = useState(
    products.filter(getAvailable),
  )

  const {
    layout: { collectionPageReviewMessage },
    product: { gender: genderText, men, women, readMore, item, items },
  } = useGlobal(['layout', 'product'], node_locale)

  // maps all genders in the products to an array
  const genderPath = ['gender', 'title']
  const genders = useMemo(() => {
    let foundGenders = R.compose(R.uniq, R.map(R.path(genderPath)))(products)
    if (foundGenders.length > 1) {
      // remove unisex from selector
      foundGenders = foundGenders.filter((g) => g.toLowerCase() !== 'unisex')
    }
    if (foundGenders.length == 1 && foundGenders.includes('Unisex')) {
      // replace unisex for men and women
      foundGenders = [men, women]
    }
    return foundGenders
  }, [genderPath, products])

  // for segment tracking: map all gender NAMES to an array
  const genderPathName = ['gender', 'name']
  const gendersName = useMemo(
    () => R.compose(R.uniq, R.map(R.path(genderPathName)))(products),
    [genderPathName, products],
  )

  // searched gender from url query
  const searchedGender = queryString.parse(search)[genderText.toLowerCase()]
  const [selectedGender, selectGender] = useState(null)
  // set the selected gender with the searched gender for every render, changes everytime with query
  useEffect(() => {
    setTimeout(() => {
      selectGender(genders.find((g) => g.toLowerCase() === searchedGender))
    }, 100)
  }, [searchedGender])

  useEffect(() => {
    setFilteredProducts(
      products.filter(getAvailable).filter((prod) => {
        if (selectedGender) {
          // show unisex socks for either women or men
          const showProduct =
            selectedGender === prod.gender.title ||
            prod.gender.name === 'Unisex'
          return showProduct ? prod : null
        }
        return prod
      }),
    )
  }, [selectedGender])

  useEffect(() => {
    setBlackFridayLayout(alternativeLayout)
  }, [alternativeLayout])

  const DynamicLayout = () =>
    !blackFridayLayout ? (
      <>
        <Header>
          <BreadCrumb>
            <SiteLink to='/'>Home</SiteLink> / {title}
          </BreadCrumb>
          <HeaderRight>
            <Try>{collectionPageReviewMessage}</Try>
          </HeaderRight>
        </Header>
        <CollectionHeadWrapper>
          <CollectionHeader>{title}</CollectionHeader>
          {detail && (
            <div>
              <CollectionDescription>
                <RichText json={detail} />
                {information && (
                  <CollectionReadMore
                    type='button'
                    onClick={() => {
                      onViewDetails()
                      useCollectionAnchorClickedTracking({
                        //This is to stop using currency from context as this is causing a re render
                        currency: currency?.code,
                        locale: node_locale,
                        collection: title,
                        label: readMore,
                      })
                    }}
                  >
                    {readMore}
                  </CollectionReadMore>
                )}
              </CollectionDescription>
              {partnerText && (
                <CollectionPartnerContainer>
                  <CollectionPartnerWrapper>
                    <CollectionPartnerText>{partnerText}</CollectionPartnerText>
                    <CollectionPartnerLink href={partnerLogoLink}>
                      {partnerLogo && partnerLogo.fluid && (
                        <CollectionPartnerLogo
                          aspectRatio={partnerLogo.fluid.aspectRatio}
                          fluid={partnerLogo.fluid}
                          imgStyle={{ objectFit: 'contain' }}
                          alt={partnerLogo.title}
                        />
                      )}
                    </CollectionPartnerLink>
                  </CollectionPartnerWrapper>
                </CollectionPartnerContainer>
              )}
            </div>
          )}
        </CollectionHeadWrapper>
        <CollectionFilter>
          {genders.map((gender) => (
            <FilterButton
              type='button'
              aria-label={`Filter products for ${gender}`}
              key={gender}
              selected={selectedGender === gender}
              onClick={() => {
                selectedGender === gender
                  ? selectGender(null)
                  : selectGender(gender)

                useCollectionGenderSelectedTracking({
                  currency: currency?.code,
                  locale: node_locale,
                  gender: gendersName[genders.indexOf(gender)],
                  collection: title,
                  label: gender,
                })
              }}
            >
              {gender}
            </FilterButton>
          ))}
        </CollectionFilter>{' '}
        <CollectionProductCount>{`${filteredProducts.length} ${
          filteredProducts.length < 2 ? item : items
        }`}</CollectionProductCount>
      </>
    ) : (
      <>
        <CollectionFilter alt>
          {genders.map((gender) => (
            <FilterButton
              type='button'
              aria-label='select filter'
              key={gender}
              selected={selectedGender === gender}
              onClick={() => {
                selectedGender === gender
                  ? selectGender(null)
                  : selectGender(gender)

                useCollectionGenderSelectedTracking({
                  currency: currency?.code,
                  locale: node_locale,
                  gender: gendersName[genders.indexOf(gender)],
                  collection: title,
                  label: gender,
                })
              }}
            >
              {gender}
            </FilterButton>
          ))}
        </CollectionFilter>
        <CollectionProductCount alt>{`${filteredProducts.length} ${
          filteredProducts.length < 2 ? item : items
        }`}</CollectionProductCount>
      </>
    )

  return (
    <StyledCollectionProducts hasSubCollection={hasSubCollection}>
      {/* <section
      style={{
        padding: isMobile
          ? `0 ${theme.space.marginMobile}vw`
          : `0 ${theme.space.marginDesktop * 12}vw
        ${theme.space.marginDesktop * 4}vw`,
        position: 'relative',
      }}
    > */}
      <DynamicLayout />
      <Grid>
        {filteredProducts.map(
          (
            {
              thumbnailImage,
              slug,
              title,
              family,
              shopifyProduct,
              thumbnailSubheading,
              label,
              logo,
            },
            index,
          ) => (
            <ProductThumbnail
              key={slug + index}
              loadingOrder={index}
              image={thumbnailImage}
              slug={slug}
              title={title}
              shopifyProduct={shopifyProduct}
              thumbnailSubheading={thumbnailSubheading}
              family={family}
              label={label}
              logo={logo}
              viewType='Collection Page'
              sectionType='Collection'
              alternativeLayout={blackFridayLayout}
            />
          ),
        )}
        {video ? (
          <CollectionVideo
            product={filteredProducts.length}
            mp4={video.videoMp4.file.url}
            webm={video.videoWebm.file.url}
            poster={video.poster.fluid}
            loading='eager'
          />
        ) : (
          <CollectionImage
            product={filteredProducts.length}
            fluid={image?.fluid}
            alt={image?.title}
            withPlaceholder
            loading='eager'
          />
        )}
      </Grid>
    </StyledCollectionProducts>
  )
}

const StyledCollectionProducts = styled.section`
  padding: 0 ${(props) => props.theme.space.marginMobile}vw;
  position: relative;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: 0 ${(props) => props.theme.space.marginDesktop * 12}vw
      ${(props) => props.theme.space.marginDesktop * 4}vw;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${(props) => props.theme.fontSize.m18};
  color: #a2a2a2;
  padding: ${(props) => props.theme.space.marginMobile}vw 0 0;
  letter-spacing: 0.05em;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    padding: ${(props) => props.theme.space.marginDesktop * 4}vw 0 0;
  }
`

const HeaderRight = styled.div`
  white-space: nowrap;
  display: flex;
  align-items: center;
`

const Try = styled.div`
  color: black;
  padding-right: ${(props) => props.theme.space.marginMobile / 4}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-right: ${(props) => props.theme.space.marginDesktop}vw;
    font-size: 0.7638888vw;
  }
`

const Review = styled(ReviewStarsStore)`
  text-transform: lowercase;
  letter-spacing: 0.05em;
  display: block;
`

const CollectionHeader = styled.h1`
  font-size: 6.666667vw;
  text-align: center;
  padding-top: ${(props) => props.theme.space.marginMobile * 2}vw;
  padding-bottom: ${(props) => props.theme.space.marginMobile / 2}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d50};
    padding-top: ${(props) => props.theme.space.marginDesktop * 2}vw;
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 1.5}vw;
  }
`

const CollectionDescription = styled.div`
  font-size: ${(props) => props.theme.fontSize.m23};
  text-align: center;
  line-height: 4.8vw;
  letter-spacing: 0.05em;
  margin: auto;

  p {
    display: inline;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d15};
    padding-bottom: 0.5vw;
    max-width: 42vw;
    line-height: 1.736vw;
    margin: auto;
  }
`
const CollectionPartnerContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 0 0 0.5vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin: 0.8vw 0 0.5vw;
  }
`

const CollectionPartnerWrapper = styled.div`
  display: flex;
  align-items: center;
`

const CollectionPartnerText = styled.div`
  font-size: ${(props) => props.theme.fontSize.m23};
  line-height: 4.8vw;
  letter-spacing: 0.05em;
  margin: auto;
  padding-right: 2vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d15};
    max-width: 42vw;
    line-height: 1;
    padding-right: 0.5vw;
  }
`

const CollectionPartnerLink = styled.a`
  display: flex;
  align-items: center;
`

const CollectionPartnerLogo = styled(GatsbyImage)`
  height: ${({ aspectRatio }) =>
    aspectRatio < 2 ? 8 : aspectRatio < 4 ? 5 : 4}vw;
  width: ${({ aspectRatio }) =>
    aspectRatio < 2
      ? aspectRatio * 8
      : aspectRatio < 4
      ? aspectRatio * 5
      : aspectRatio * 4}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: ${({ aspectRatio }) =>
      aspectRatio < 2 ? 3 : aspectRatio < 4 ? 1.5 : 1}vw;
    width: ${({ aspectRatio }) =>
      aspectRatio < 2
        ? aspectRatio * 3
        : aspectRatio < 4
        ? aspectRatio * 1.5
        : aspectRatio}vw;
  }
`

const CollectionReadMore = styled.button`
  margin: 0 2vw;
  color: #a2a2a2;
  cursor: pointer;
  display: inline-block;
  text-transform: lowercase;
  ${(props) => props.theme.underline('#a2a2a2', '0.05em')}
  font-size: ${(props) => props.theme.fontSize.m23};
  letter-spacing: 0.05em;
  text-align: center;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    margin: 0 ${(props) => props.theme.space.marginDesktop}vw;
  }
`

const CollectionHeadWrapper = styled.div`
  padding-bottom: 4vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 4}vw;
  }
`

const CollectionProductCount = styled.div`
  font-size: ${(props) => props.theme.fontSize.m20};
  letter-spacing: 0.05em;
  color: #a2a2a2;
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  text-transform: lowercase;
  position: ${(props) => (props.alt ? 'absolute' : 'static')};
  top: ${(props) => (props.alt ? '12vw' : null)};
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 3}vw;
    top: ${(props) => (props.alt ? '-3.2vw' : null)};
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 3vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    grid-template-columns: repeat(4, 1fr);
    grid-gap: ${(props) => props.theme.space.marginDesktop * 4}vw
      ${(props) => props.theme.space.marginDesktop * 2}vw;
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    display: flex;
    flex-wrap: wrap;

    align-items: flex-start;
    align-content: flex-start;

    a {
      flex-basis: 23%;
      margin-right: 1.5vw;
      margin-bottom: 2.76vw;
    }

    & > div {
      height: 27vw;
    }
  }
`

const CollectionImage = styled(GatsbyImage)`
  grid-area: 2 / 2 / 3 / 3;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    grid-column: ${(props) => (props.product < 3 ? 'auto' : '2 / -1')};
    grid-column-start: ${(props) =>
      props.product < 3 ? props.product + 1 : 4};
    grid-row-start: 1;
    grid-row-end: ${(props) => (props.product < 3 ? 'auto' : 1)};
    height: 27vw;
    ${(props) => props.product};
    ${(props) =>
      props.product > 8 &&
      css`
        grid-column: '2 / -1';
        grid-column-start: 3;
        grid-row-start: 2;
        grid-row-end: 4;
        height: 62.4vw;
      `}
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    flex-basis: 23%;
    margin-right: 1.5vw;
    margin-bottom: 2.76vw;
    overflow: hidden;

    img {
      width: auto !important;
      position: absolute !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) !important;
    }
  }
`

const CollectionFilter = styled.div`
  margin: auto;
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: ${(props) => (props.alt ? '10vw' : '6vw')};
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-bottom: ${(props) =>
      props.alt
        ? props.theme.space.marginDesktop * 4
        : -props.theme.space.marginDesktop * 1.7}vw;
  }
`

const FilterButton = styled.button`
  font-size: ${(props) => props.theme.fontSize.m23};
  letter-spacing: 0.05em;
  background: ${(props) =>
    props.selected ? props.theme.colors.blue : props.theme.colors.lightBlue};
  color: ${(props) => (props.selected ? 'white' : props.theme.colors.blue)};
  height: ${(props) => props.theme.space.marginMobile * 2}vw;
  line-height: ${(props) => props.theme.space.marginMobile * 2}vw;
  width: 28vw;
  border-radius: ${(props) => props.theme.space.marginMobile * 5}vw;
  text-transform: lowercase;
  cursor: pointer;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    height: 1.7361111111vw;
    line-height: 1.7361111111vw;
    width: ${(props) => props.theme.space.marginDesktop * 7}vw;
  }

  &:not(:first-of-type) {
    margin-left: ${(props) => props.theme.space.marginMobile / 2}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      margin-left: ${(props) => props.theme.space.marginDesktop}vw;
    }
  }
`

const CollectionVideo = styled(Video)`
  grid-area: 2 / 2 / 3 / 3;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    grid-area: 2 / 3 / 4 / 5;
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    flex-basis: 23%;
    margin-right: 1.5vw;
    margin-bottom: 2.76vw;
    overflow: hidden;
  }
`

export default CollectionProducts
